import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge, DocExpiryBadge } from "@/components/StatusBadge";
import { ROLE_LABEL, VEHICLE_STATUS_LABEL, ORDER_CATEGORY_LABEL, fmtDate } from "@/lib/format";
import { AlertTriangle, FileWarning, Wrench, Truck } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);

  if (!data) return <div className="p-8 text-gray-500">Cargando...</div>;

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        tag={ROLE_LABEL[user.role]}
        title={`Hola, ${user.name?.split(" ")[0] || "usuario"}.`}
        description="Vista general de tu operación. Alertas, vencimientos y trabajos pendientes."
      />

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 border border-gray-200 mb-10">
        <Tile label="Vehículos" value={data.total_vehicles ?? 0} icon={Truck} testId="kpi-vehicles" />
        <Tile label="Vencidos" value={(data.docs_expired || []).length} icon={FileWarning} accent="danger" testId="kpi-expired" />
        <Tile label="Por vencer (30d)" value={(data.docs_critical || []).length + (data.docs_expiring || []).length} icon={AlertTriangle} accent="warning" testId="kpi-expiring" />
        <Tile label="Órdenes pendientes" value={(data.pending_orders || []).length} icon={Wrench} accent="primary" testId="kpi-orders" />
      </div>

      {/* Status counts (rent-a-car) */}
      {user.role === "rentacar_admin" && data.status_counts && (
        <div className="mb-10">
          <p className="veta-tag mb-3">Estado de la flota</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(data.status_counts).map(([k, v]) => (
              <div key={k} className="border border-gray-200 p-5" data-testid={`status-tile-${k}`}>
                <p className="veta-tag">{VEHICLE_STATUS_LABEL[k]}</p>
                <p className="font-display text-3xl mt-2">{v}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-px bg-gray-200 border border-gray-200">
        {/* Documents */}
        <div className="bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="veta-tag">Documentos</p>
            <Link to="/flota" className="text-xs font-bold uppercase tracking-wider text-blue-600 underline">Ver flota</Link>
          </div>
          <h3 className="font-display text-2xl mb-4">Vencimientos próximos</h3>
          <div className="divide-y divide-gray-200">
            {[...(data.docs_expired || []), ...(data.docs_critical || []), ...(data.docs_expiring || [])]
              .slice(0, 8)
              .map((d) => (
                <div key={d.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-mono text-sm text-black truncate">
                      <span className="font-bold">{d.plate}</span> · {d.doc_type}
                    </p>
                    <p className="text-xs text-gray-500">Vence: {fmtDate(d.expiry_date)}</p>
                  </div>
                  <DocExpiryBadge expiryDate={d.expiry_date} />
                </div>
              ))}
            {[...(data.docs_expired || []), ...(data.docs_critical || []), ...(data.docs_expiring || [])].length === 0 && (
              <p className="text-sm text-gray-500 py-6">Sin alertas — todo al día.</p>
            )}
          </div>
        </div>

        {/* Pending Orders */}
        <div className="bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="veta-tag">Taller</p>
            <Link to="/taller" className="text-xs font-bold uppercase tracking-wider text-blue-600 underline">Ver órdenes</Link>
          </div>
          <h3 className="font-display text-2xl mb-4">Órdenes pendientes</h3>
          <div className="divide-y divide-gray-200">
            {(data.pending_orders || []).slice(0, 8).map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-black truncate">
                    <span className="font-bold">{o.plate}</span> · {ORDER_CATEGORY_LABEL[o.category] || o.category}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{o.description}</p>
                </div>
                <StatusBadge status={o.status === "open" ? "warning" : "neutral"} label={o.status === "open" ? "Abierta" : "En progreso"} />
              </div>
            ))}
            {(data.pending_orders || []).length === 0 && (
              <p className="text-sm text-gray-500 py-6">No hay órdenes pendientes.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, icon: Icon, accent, testId }) {
  const accentBg = {
    danger: "bg-red-50",
    warning: "bg-yellow-50",
    primary: "bg-blue-50",
  }[accent] || "bg-white";
  return (
    <div className={`${accentBg} p-6 flex items-start justify-between gap-3`} data-testid={testId}>
      <div>
        <p className="veta-tag">{label}</p>
        <p className="font-display text-4xl md:text-5xl tracking-tighter mt-2">{value}</p>
      </div>
      {Icon && <Icon size={24} strokeWidth={2.5} className="text-black" />}
    </div>
  );
}
